import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export const Register = () => {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        company_name: '',
        contact_name: '',
        phone: '',
        address: '',
        siret: '',
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const normalizedData = { ...formData, password: formData.password.normalize('NFC').trim(), email: formData.email.trim() };
            await register(normalizedData);
            toast.success('Compte créé avec succès !');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-muted/50 flex items-start justify-center px-4 py-8 pb-60" data-testid="register-page">
            <Card className="w-full max-w-lg">
                <CardHeader className="text-center">
                    <div className="w-12 h-12 mx-auto rounded-lg bg-leaf flex items-center justify-center mb-4">
                        <Package className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="font-heading text-2xl">Créer un compte</CardTitle>
                    <CardDescription>
                        Rejoignez PRIXDEAL et accédez à nos tarifs professionnels
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="company_name">Nom de l'entreprise *</Label>
                                <Input
                                    id="company_name"
                                    placeholder="Ex: Franprix Paris 15"
                                    value={formData.company_name}
                                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                    required
                                    data-testid="company-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="siret">SIRET</Label>
                                <Input
                                    id="siret"
                                    placeholder="12345678901234"
                                    value={formData.siret}
                                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                                    data-testid="siret-input"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="contact_name">Nom du contact *</Label>
                                <Input
                                    id="contact_name"
                                    placeholder="Jean Dupont"
                                    value={formData.contact_name}
                                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                                    required
                                    data-testid="contact-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone">Téléphone *</Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    placeholder="01 23 45 67 89"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    required
                                    data-testid="phone-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="address">Adresse de livraison *</Label>
                            <Input
                                id="address"
                                placeholder="123 Rue de la Convention, 75015 Paris"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                required
                                data-testid="address-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">Email professionnel *</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="contact@votre-entreprise.fr"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                data-testid="email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Mot de passe *</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    required
                                    minLength={6}
                                    data-testid="password-input"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-0 top-0 h-full"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full bg-leaf hover:bg-leaf/90 text-white font-semibold" 
                            disabled={loading}
                            data-testid="register-submit"
                        >
                            {loading ? 'Création...' : 'Créer mon compte'}
                        </Button>
                    </form>

                    <div className="mt-6 text-center text-sm">
                        <span className="text-muted-foreground">Déjà un compte ? </span>
                        <Link to="/connexion" className="text-leaf hover:underline font-medium" data-testid="login-link">
                            Se connecter
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
